import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Star, LogOut, Loader2, Ticket, Plus, Copy, Check, Ban, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const PLAN_LABELS = {
  free: 'Kostenlos',
  standard: 'Standard',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

export default function AdminCoupons() {
  const { user, loading: authLoading, logout } = useAuth('admin_user');
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, redemptions: 0 });
  const [plans, setPlans] = useState(['free', 'standard', 'premium', 'enterprise']);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const [form, setForm] = useState({
    code: '',
    plan: 'premium',
    duration_days: '',
    max_redemptions: 1,
    note: '',
  });

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const load = async () => {
    try {
      const res = await adminApi.getCoupons();
      setCoupons(res.data.coupons || []);
      setStats(res.data.stats || { total: 0, active: 0, redemptions: 0 });
      if (res.data.available_plans?.length) setPlans(res.data.available_plans);
    } catch {
      toast.error('Fehler beim Laden der Gutscheine');
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    setCreating(true);
    try {
      const payload = {
        plan: form.plan,
        max_redemptions: Number(form.max_redemptions) || 0,
        note: form.note || null,
      };
      if (form.code.trim()) payload.code = form.code.trim();
      if (form.duration_days) payload.duration_days = Number(form.duration_days);

      const res = await adminApi.createCoupon(payload);
      toast.success(`Gutschein ${res.data.code} erstellt`);
      setForm({ code: '', plan: 'premium', duration_days: '', max_redemptions: 1, note: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await adminApi.updateCoupon(coupon.id, { active: !coupon.active });
      toast.success(coupon.active ? 'Gutschein deaktiviert' : 'Gutschein aktiviert');
      load();
    } catch {
      toast.error('Fehler beim Ändern');
    }
  };

  const remove = async (coupon) => {
    if (!window.confirm(`Gutschein ${coupon.code} wirklich löschen?`)) return;
    try {
      const res = await adminApi.deleteCoupon(coupon.id);
      toast.success(res.data.deleted ? 'Gutschein gelöscht' : res.data.detail);
      load();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const copyCode = (coupon) => {
    navigator.clipboard.writeText(coupon.code);
    setCopiedId(coupon.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Code kopiert');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-primary text-primary-foreground border-b">
        <div className="container-main flex items-center justify-between h-16">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <Star className="w-5 h-5" />
            </div>
            <span className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Immoverita Admin</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="text-sm opacity-80 hover:opacity-100">Übersicht</Link>
            <Link to="/admin/tenants" className="text-sm opacity-80 hover:opacity-100">Tenants</Link>
            <Link to="/admin/reviews" className="text-sm opacity-80 hover:opacity-100">Bewertungen</Link>
            <Link to="/admin/prompts" className="text-sm opacity-80 hover:opacity-100">Prompts</Link>
            <Link to="/admin/coupons" className="text-sm font-medium">Gutscheine</Link>
          </nav>
          <Button variant="ghost" size="sm" onClick={logout} className="text-primary-foreground hover:bg-white/10">
            <LogOut className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
        </div>
      </header>

      <main className="container-main py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Ticket className="w-6 h-6 text-primary" />
            Gutscheine
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pilotkunden einen Plan freischalten — befristet oder dauerhaft.
          </p>
        </div>

        {/* Kennzahlen */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Gutscheine', value: stats.total },
            { label: 'Aktiv', value: stats.active },
            { label: 'Einlösungen', value: stats.redemptions },
          ].map(({ label, value }) => (
            <Card key={label}>
              <CardContent className="pt-5 pb-4 text-center">
                <p className="text-2xl font-bold">{value}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Anlegen */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Neuen Gutschein anlegen</CardTitle>
            <CardDescription>Code leer lassen, um automatisch einen zu erzeugen.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Code (optional)</label>
                <Input
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="z. B. PILOT2026"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Plan</label>
                <select
                  value={form.plan}
                  onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {plans.map(p => (
                    <option key={p} value={p}>{PLAN_LABELS[p] || p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Laufzeit (Tage)</label>
                <Input
                  type="number"
                  min="1"
                  value={form.duration_days}
                  onChange={e => setForm(f => ({ ...f, duration_days: e.target.value }))}
                  placeholder="leer = unbefristet"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Max. Einlösungen</label>
                <Input
                  type="number"
                  min="0"
                  value={form.max_redemptions}
                  onChange={e => setForm(f => ({ ...f, max_redemptions: e.target.value }))}
                  placeholder="0 = unbegrenzt"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Notiz (intern)</label>
              <Input
                value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                placeholder="z. B. Pilotkunde Muster HV, zugesagt von Steffen"
              />
            </div>
            <Button onClick={create} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Gutschein erstellen
            </Button>
          </CardContent>
        </Card>

        {/* Liste */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Alle Gutscheine</CardTitle>
          </CardHeader>
          <CardContent>
            {coupons.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Noch keine Gutscheine angelegt.</p>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Laufzeit</TableHead>
                      <TableHead>Einlösungen</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Notiz</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {coupons.map(c => {
                      const exhausted = c.max_redemptions > 0 && c.redemptions >= c.max_redemptions;
                      return (
                        <TableRow key={c.id}>
                          <TableCell>
                            <button
                              onClick={() => copyCode(c)}
                              className="font-mono font-semibold text-sm inline-flex items-center gap-1.5 hover:text-primary transition-colors"
                              title="Code kopieren"
                            >
                              {c.code}
                              {copiedId === c.id
                                ? <Check className="w-3.5 h-3.5 text-green-600" />
                                : <Copy className="w-3.5 h-3.5 opacity-40" />}
                            </button>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{PLAN_LABELS[c.plan] || c.plan}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {c.duration_days ? `${c.duration_days} Tage` : 'unbefristet'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {c.redemptions}{c.max_redemptions > 0 ? ` / ${c.max_redemptions}` : ' / ∞'}
                          </TableCell>
                          <TableCell>
                            {!c.active
                              ? <Badge variant="outline" className="text-muted-foreground">inaktiv</Badge>
                              : exhausted
                                ? <Badge variant="outline" className="text-orange-600 border-orange-300">aufgebraucht</Badge>
                                : <Badge className="bg-green-100 text-green-800 hover:bg-green-100">aktiv</Badge>}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                            {c.note || '—'}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Button variant="ghost" size="sm" onClick={() => toggleActive(c)} title={c.active ? 'Deaktivieren' : 'Aktivieren'}>
                              {c.active ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-destructive" title="Löschen">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
